import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Mail, Sparkles, Copy, Download, Eye, FileText,
  Building2, User, Target, BarChart3, Send, RefreshCw,
  Check, Briefcase, TrendingUp, Heart, FileCheck, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TEMPLATE_ICONS = {
  investor_update: TrendingUp,
  partnership_outreach: Briefcase,
  funding_announcement: Sparkles,
  corporate_introduction: Building2,
  thank_you: Heart,
  quarterly_report: FileCheck
};

export default function EmailCampaignBuilder() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState({});
  const [ceoInfo, setCeoInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState(null);
  const [htmlPreview, setHtmlPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('compose');
  
  // Form state
  const [formData, setFormData] = useState({
    template_type: 'investor_update',
    recipient_name: '',
    recipient_company: '',
    subject_line: '',
    key_points: '',
    custom_prompt: '',
    include_metrics: false,
    metrics: {
      users: '',
      revenue: '',
      growth: ''
    }
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-campaign/templates`);
      setTemplates(response.data.templates);
      setCeoInfo(response.data.ceo_info);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMetricChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [field]: value
      }
    }));
  };

  const generateEmail = async () => {
    if (!formData.recipient_name) {
      toast.error('Please enter a recipient name');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        template_type: formData.template_type,
        recipient_name: formData.recipient_name,
        recipient_company: formData.recipient_company || null,
        subject_line: formData.subject_line || null,
        key_points: formData.key_points ? formData.key_points.split('\n').filter(p => p.trim()) : null,
        custom_prompt: formData.custom_prompt || null,
        include_metrics: formData.include_metrics,
        metrics: formData.include_metrics ? formData.metrics : null
      };

      const response = await axios.post(`${API}/email-campaign/generate`, payload);
      setGeneratedEmail(response.data);
      
      // Also get HTML preview
      const htmlResponse = await axios.post(`${API}/email-campaign/preview-html`, payload);
      setHtmlPreview(htmlResponse.data);
      
      setActiveTab('preview');
      toast.success('Email generated successfully!');
    } catch (error) {
      console.error('Failed to generate email:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadHtml = () => {
    if (!htmlPreview?.html) return;
    
    const blob = new Blob([htmlPreview.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-${formData.template_type}-${Date.now()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('HTML downloaded!');
  };

  const selectedTemplate = templates[formData.template_type];
  const TemplateIcon = TEMPLATE_ICONS[formData.template_type] || Mail;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Mail className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Email Campaign Builder</h1>
                <p className="text-sm text-zinc-400">AI-Powered Corporate Communications</p>
              </div>
            </div>
            {ceoInfo && (
              <div className="flex items-center gap-3">
                <img 
                  src={ceoInfo.image_url} 
                  alt={ceoInfo.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-amber-500"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-semibold">{ceoInfo.name}</p>
                  <p className="text-xs text-zinc-400">{ceoInfo.title}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="compose" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
              <FileText className="w-4 h-4 mr-2" />
              Compose
            </TabsTrigger>
            <TabsTrigger value="preview" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" disabled={!generatedEmail}>
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </TabsTrigger>
          </TabsList>

          {/* Compose Tab */}
          <TabsContent value="compose" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Template Selection */}
              <div className="lg:col-span-1 space-y-4">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Target className="w-5 h-5 text-amber-400" />
                      Email Template
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {Object.entries(templates).map(([key, template]) => {
                      const Icon = TEMPLATE_ICONS[key] || Mail;
                      const isSelected = formData.template_type === key;
                      return (
                        <button
                          key={key}
                          onClick={() => handleInputChange('template_type', key)}
                          className={`w-full p-3 rounded-lg border text-left transition-all ${
                            isSelected 
                              ? 'border-amber-500 bg-amber-500/10' 
                              : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${isSelected ? 'text-amber-400' : 'text-zinc-400'}`} />
                            <div>
                              <p className={`font-medium ${isSelected ? 'text-amber-400' : 'text-white'}`}>
                                {template.name}
                              </p>
                              <p className="text-xs text-zinc-500">{template.description}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>

                {selectedTemplate && (
                  <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TemplateIcon className="w-4 h-4 text-amber-400" />
                        <span className="text-amber-400 font-semibold text-sm">Selected Template</span>
                      </div>
                      <p className="text-sm text-zinc-300">{selectedTemplate.description}</p>
                      <p className="text-xs text-zinc-500 mt-2">Tone: {selectedTemplate.tone}</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Email Details Form */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="w-5 h-5 text-amber-400" />
                      Recipient Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-400">Recipient Name *</Label>
                        <Input
                          value={formData.recipient_name}
                          onChange={(e) => handleInputChange('recipient_name', e.target.value)}
                          placeholder="John Smith"
                          className="bg-zinc-800 border-zinc-700 mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400">Company</Label>
                        <Input
                          value={formData.recipient_company}
                          onChange={(e) => handleInputChange('recipient_company', e.target.value)}
                          placeholder="Acme Ventures"
                          className="bg-zinc-800 border-zinc-700 mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-400">Subject Line Hint (optional)</Label>
                      <Input
                        value={formData.subject_line}
                        onChange={(e) => handleInputChange('subject_line', e.target.value)}
                        placeholder="e.g., Q4 Growth Update, Partnership Opportunity..."
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-amber-400" />
                      Content Guidance
                    </CardTitle>
                    <CardDescription>Help the AI craft the perfect message</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-zinc-400">Key Points (one per line)</Label>
                      <Textarea
                        value={formData.key_points}
                        onChange={(e) => handleInputChange('key_points', e.target.value)}
                        placeholder="Strong Q4 performance&#10;New partnerships announced&#10;Expansion plans for 2026"
                        className="bg-zinc-800 border-zinc-700 mt-1 min-h-[100px]"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Additional Context (optional)</Label>
                      <Textarea
                        value={formData.custom_prompt}
                        onChange={(e) => handleInputChange('custom_prompt', e.target.value)}
                        placeholder="Any specific details, tone adjustments, or context the AI should know..."
                        className="bg-zinc-800 border-zinc-700 mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-amber-400" />
                      Include Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 mb-4">
                      <input
                        type="checkbox"
                        id="include-metrics"
                        checked={formData.include_metrics}
                        onChange={(e) => handleInputChange('include_metrics', e.target.checked)}
                        className="w-4 h-4 rounded border-zinc-600 text-amber-500 focus:ring-amber-500"
                      />
                      <Label htmlFor="include-metrics" className="text-zinc-300 cursor-pointer">
                        Include business metrics in the email
                      </Label>
                    </div>
                    {formData.include_metrics && (
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-zinc-400">Active Users</Label>
                          <Input
                            value={formData.metrics.users}
                            onChange={(e) => handleMetricChange('users', e.target.value)}
                            placeholder="10,000+"
                            className="bg-zinc-800 border-zinc-700 mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-400">Revenue/MRR</Label>
                          <Input
                            value={formData.metrics.revenue}
                            onChange={(e) => handleMetricChange('revenue', e.target.value)}
                            placeholder="$50K MRR"
                            className="bg-zinc-800 border-zinc-700 mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-400">Growth Rate</Label>
                          <Input
                            value={formData.metrics.growth}
                            onChange={(e) => handleMetricChange('growth', e.target.value)}
                            placeholder="25% MoM"
                            className="bg-zinc-800 border-zinc-700 mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Generate Button */}
                <Button
                  onClick={generateEmail}
                  disabled={loading || !formData.recipient_name}
                  className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold text-lg"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Generating with AI...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Email with AI
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="space-y-6">
            {generatedEmail && (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Plain Text Preview */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-amber-400" />
                        Plain Text
                      </CardTitle>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(generatedEmail.body)}
                        className="border-zinc-700"
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="p-3 bg-zinc-800 rounded-lg">
                        <p className="text-xs text-zinc-500 mb-1">Subject</p>
                        <p className="text-white font-medium">{generatedEmail.subject}</p>
                      </div>
                      <div className="p-3 bg-zinc-800 rounded-lg">
                        <p className="text-xs text-zinc-500 mb-1">Preview Text</p>
                        <p className="text-zinc-300 text-sm">{generatedEmail.preview_text}</p>
                      </div>
                      <div className="p-4 bg-zinc-800 rounded-lg max-h-[400px] overflow-y-auto">
                        <p className="text-xs text-zinc-500 mb-2">Body</p>
                        <pre className="text-zinc-300 text-sm whitespace-pre-wrap font-sans">
                          {generatedEmail.body}
                        </pre>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* HTML Preview */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Eye className="w-5 h-5 text-amber-400" />
                        HTML Preview
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(htmlPreview?.html || '')}
                          className="border-zinc-700"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy HTML
                        </Button>
                        <Button
                          size="sm"
                          onClick={downloadHtml}
                          className="bg-amber-500 hover:bg-amber-600 text-black"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {htmlPreview?.html && (
                      <div className="bg-white rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                        <iframe
                          srcDoc={htmlPreview.html}
                          title="Email Preview"
                          className="w-full h-[500px] border-0"
                          sandbox="allow-same-origin"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Actions */}
            {generatedEmail && (
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={() => setActiveTab('compose')}
                  className="border-zinc-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
                <Button
                  onClick={() => {
                    // Open in new window for printing/sending
                    const newWindow = window.open('', '_blank');
                    newWindow.document.write(htmlPreview?.html || '');
                    newWindow.document.close();
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-black"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Open Full Preview
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* CEO Signature Preview */}
        {ceoInfo && (
          <Card className="mt-8 bg-zinc-900/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg">CEO Signature Block</CardTitle>
              <CardDescription>This signature is automatically included in all generated emails</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4 p-4 bg-zinc-800 rounded-lg">
                <img 
                  src={ceoInfo.image_url} 
                  alt={ceoInfo.name}
                  className="w-20 h-20 rounded-full object-cover border-3 border-amber-500"
                />
                <div>
                  <p className="font-serif text-2xl text-zinc-300 italic">George Broughton</p>
                  <p className="text-zinc-400 mt-1">Founder & CEO</p>
                  <p className="text-zinc-500">MyHairChair</p>
                  <div className="flex gap-3 mt-2">
                    <a href={`mailto:${ceoInfo.email}`} className="text-amber-400 text-sm hover:underline">
                      {ceoInfo.email}
                    </a>
                    <span className="text-zinc-600">|</span>
                    <a href="https://myhairchair.com" className="text-amber-400 text-sm hover:underline">
                      myhairchair.com
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
